import { useIntl } from "react-intl";
import { useMemo, useState } from "react";
import { Popconfirm, Space, TablePaginationConfig, TableProps } from "antd";
import { NumberParam, useQueryParams } from "use-query-params";
import { useVenueListQuery } from "@ticketingplatform/api/dist/venue/hooks";
import { pageToSkip } from "utils/tableUtils";
import { ColumnsType } from "antd/es/table";
import { currencyFormatter } from "utils/functions";
import { GetVenues } from "@ticketingplatform/api/dist/venue/__generated__/queries";
import { generatePath, Link } from "react-router-dom";
import { EditOutlined, EyeOutlined } from "@ant-design/icons";
import { DeleteIcon } from "assets/icons";
import { ROUTE_PATHS } from "const/paths";
import useHasPermissions from "hooks/useHasPermissions";
import { Permission } from "@ticketingplatform/api/dist/__generated__/globalTypes";

import { useVenueDelete } from "../hooks/useVenueDelete";

import { UseVenueColumnReturnType, UseVenueListReturnType } from "./types";
import { PAGE_SIZE } from "./consts";

type GetVenues_venues_nodes = NonNullable<GetVenues["venues"]>["nodes"][number];

export const useVenueColumns = (): UseVenueColumnReturnType => {
  const intl = useIntl();
  const { deleteVenue } = useVenueDelete();
  const canDeleteVenue = useHasPermissions([Permission.VENUE_DELETE]);

  const columns: ColumnsType<GetVenues_venues_nodes> = useMemo(
    () => [
      {
        title: intl.formatMessage({
          defaultMessage: "Venue name",
        }),
        dataIndex: "name",
        key: "name",
        render: (_, record) => {
          const id = record.id.toString();
          const venueDetailsRoute = generatePath(ROUTE_PATHS.VenueDetails, {
            id,
          });

          return <Link to={venueDetailsRoute}>{record.name}</Link>;
        },
      },
      {
        title: intl.formatMessage({
          defaultMessage: "Platform fee",
        }),
        key: "platformFee",
        render: (_, record) => (
          <Space size="middle">{currencyFormatter(record.platformFee)}</Space>
        ),
      },
      {
        title: intl.formatMessage({
          defaultMessage: "Organization name",
        }),
        key: "platformFee",
        render: (_, record) => (
          <Space size="middle">{record.organization?.name} </Space>
        ),
      },
      {
        title: intl.formatMessage({
          defaultMessage: "City",
        }),
        key: "city",
        render: (_, record) => (
          <Space size="middle">{record.address?.city || "-"}</Space>
        ),
      },
      {
        title: intl.formatMessage({
          defaultMessage: "Actions",
        }),
        key: "action",
        render: (_, record) => {
          const id = record.id.toString();
          const venueEditRoute = generatePath(ROUTE_PATHS.VenueEdit, { id });
          const venueDetailsRoute = generatePath(ROUTE_PATHS.VenueDetails, {
            id,
          });

          return (
            <Space size="middle">
              <Link to={venueEditRoute}>
                <EditOutlined />
              </Link>
              <Link to={venueDetailsRoute}>
                <EyeOutlined />
              </Link>
              {canDeleteVenue && (
                <Popconfirm
                  title={intl.formatMessage({
                    defaultMessage: "Are you sure you want to delete venue?",
                  })}
                  onConfirm={() => deleteVenue(record.id)}
                >
                  <DeleteIcon />
                </Popconfirm>
              )}
            </Space>
          );
        },
      },
    ],
    [canDeleteVenue, deleteVenue, intl]
  );

  return { columns };
};

export const useVenueList = (): UseVenueListReturnType => {
  const [filters, setFilters] = useQueryParams({
    current: NumberParam,
    pageSize: NumberParam,
  });
  const [pagination, setPagination] = useState<TablePaginationConfig>({
    current: 1,
  });

  const { data, loading } = useVenueListQuery({
    variables: {
      organizationInclude: true,
      addressInclude: true,
      take: filters.pageSize,
      skip: pageToSkip(filters.current || 1, PAGE_SIZE),
    },

    onCompleted: (_data) => {
      setPagination({
        ...pagination,
        total: _data?.venues?.count,
      });
      setFilters({
        current: filters.current || 1,
        pageSize: pagination.pageSize || PAGE_SIZE,
      });
    },
  });

  const tableChangeHandler: TableProps<GetVenues_venues_nodes>["onChange"] = (
    _pagination
  ) => {
    setPagination(_pagination);
    setFilters(_pagination);
  };

  const venues = data?.venues?.nodes || [];

  return {
    tableChangeHandler,
    loading,
    pagination: {
      ...pagination,
      current: filters.current || 1,
      pageSize: filters.pageSize || PAGE_SIZE,
    },
    venues,
  };
};
